/**
 * Alerts panel grouping: severity tier per alert, grouping alerts of the same type, nesting
 * OrderLate alerts under their TruckLate alert, sort order, summaries and counts.
 * Framework-free. From specs/designs/alerts-panel-steel-handoff/alerts/alert-grouping.ts; the rules
 * are in that handoff's docs/alerts-panel-handoff.md § Grouping rules, and alert-grouping.spec.ts
 * (ported from its tests) is the source of truth.
 */

export type AlertTier = 'critical' | 'warning' | 'notice';

/** The subset of `IAlert` (core/dto.ts) this module reads. */
export interface AlertLike {
  id: string;
  isCritical: boolean;
  alertType: string;
  title?: string;
  /** Set by the server for alert types whose title alone isn't the subject. */
  subject?: string;
  subTitle?: string;
  description?: string;
  sref?: string;
  referenceDocumentId?: string;
  created?: string;
  effectiveDate?: string;
  expectedDate?: string;
  machineNumber?: number;
  machine?: { description?: string } | null;
}

export interface AlertItem<A extends AlertLike = AlertLike> {
  alert: A;
  /** What the alert is about: machine, truck, order, pattern or location. */
  subject: string;
  /** The date the item sorts by and shows (expected/effective/created); null when missing or a placeholder. */
  when: Date | null;
  /** OrderLate alerts nested under a TruckLate item. Empty for every other type. */
  children: AlertItem<A>[];
}

export interface AlertGroup<A extends AlertLike = AlertLike> {
  /** Stable key for expand state: `${tier}|${type}`. */
  key: string;
  tier: AlertTier;
  /** The alert type the group is named after (OrderLate folds into TruckLate). */
  type: string;
  items: AlertItem<A>[];
  /** Top-level items; nested orders are not counted. */
  count: number;
  /** Orders nested under trucks (TruckLate groups only). */
  childCount: number;
  /** More than one item, or one item with children: render a chevron and an item list. */
  expandable: boolean;
  /** Distinct subjects, at most `SUMMARY_MAX`, for the collapsed one-line summary. */
  summarySubjects: string[];
  /** Subjects left out of `summarySubjects` ("+N more"). */
  summaryMore: number;
}

export interface AlertSection<A extends AlertLike = AlertLike> {
  tier: AlertTier;
  count: number;
  groups: AlertGroup<A>[];
}

export const TIER_ORDER: AlertTier[] = ['critical', 'warning', 'notice'];

/** Types shown as Notices (informational) when not critical. */
export const NOTICE_TYPES: string[] = ['Licensing', 'GeneralInfo'];

/** Types whose subject is the machine. */
export const MACHINE_TYPES: string[] = [
  'MachineOffline',
  'AgentOffline',
  'AgentConfiguration',
  'CommOffline',
  'CommError',
  'ScheduleSlip',
  'ProductionSyncStatus',
  'StartupSyncStatus',
];

/** Group order inside a tier. Types not listed sort after these, by name. */
export const TYPE_ORDER: string[] = [
  'SystemError',
  'MachineOffline',
  'AgentOffline',
  'CommOffline',
  'CommError',
  'PrinterError',
  'TaskError',
  'IntegrationError',
  'SolutionError',
  'ScheduleSendError',
  'TruckLate',
  'ScheduleSlip',
  'WarehouseTaskLate',
  'Licensing',
  'GeneralInfo',
];

export const SUMMARY_MAX = 4;

/** The server decides what is critical (it flags Machine Offline, for instance). */
export function tierOf(a: AlertLike): AlertTier {
  if (a.isCritical) return 'critical';
  if (NOTICE_TYPES.includes(a.alertType)) return 'notice';
  return 'warning';
}

/** OrderLate folds into the TruckLate group. */
export function groupTypeOf(a: AlertLike): string {
  return a.alertType === 'OrderLate' ? 'TruckLate' : a.alertType;
}

/**
 * A date the server sent, or null when missing, unparsable or a placeholder such as
 * DateTime.MinValue (01/01/0001), which today renders as "LATE BY: 2026 years".
 */
export function validDate(s: string | undefined | null): Date | null {
  if (!s) return null;
  const d = new Date(s);
  if (isNaN(d.getTime()) || d.getFullYear() < 2000) return null;
  return d;
}

export function whenOf(a: AlertLike): Date | null {
  return validDate(a.expectedDate) ?? validDate(a.effectiveDate) ?? validDate(a.created);
}

/**
 * The thing the alert is about: the server's `subject` ("NORTH" for "Late truck: NORTH"); for
 * machine types without one, the machine's description (the server sends machine number 0 for "no
 * machine"); otherwise the whole title.
 */
export function subjectOf(a: AlertLike): string {
  const subject = a.subject?.trim();
  if (subject) return subject;
  if (MACHINE_TYPES.includes(a.alertType)) {
    const d = a.machine?.description?.trim();
    if (d) return d;
    if ((a.machineNumber ?? 0) > 0) return `Machine ${a.machineNumber}`;
  }
  return (a.title ?? '').trim();
}

const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

/** Oldest date first (longest outstanding), missing dates last, then subject. */
export function compareItems(x: AlertItem, y: AlertItem): number {
  if (x.when && y.when && x.when.getTime() !== y.when.getTime()) {
    return x.when.getTime() - y.when.getTime();
  }
  if (x.when && !y.when) return -1;
  if (!x.when && y.when) return 1;
  return collator.compare(x.subject, y.subject);
}

function typeRank(t: string): number {
  const i = TYPE_ORDER.indexOf(t);
  return i < 0 ? TYPE_ORDER.length : i;
}

export function compareGroups(x: AlertGroup, y: AlertGroup): number {
  const r = typeRank(x.type) - typeRank(y.type);
  return r !== 0 ? r : collator.compare(x.type, y.type);
}

function toItem<A extends AlertLike>(a: A): AlertItem<A> {
  return { alert: a, subject: subjectOf(a), when: whenOf(a), children: [] };
}

/** Builds the panel: tiers in order, empty tiers left out. */
export function buildSections<A extends AlertLike>(alerts: A[]): AlertSection<A>[] {
  const byKey = new Map<string, { tier: AlertTier; type: string; alerts: A[] }>();
  for (const a of alerts) {
    const tier = tierOf(a);
    const type = groupTypeOf(a);
    const key = `${tier}|${type}`;
    let g = byKey.get(key);
    if (!g) byKey.set(key, (g = { tier, type, alerts: [] }));
    g.alerts.push(a);
  }

  const groups: AlertGroup<A>[] = [];
  for (const [key, g] of byKey) {
    let items: AlertItem<A>[];
    let childCount = 0;
    if (g.type === 'TruckLate') {
      // Orders nest under the truck alert with the same sref (`/schedule/trucks?truck=<instance>`).
      const trucks = g.alerts.filter((a) => a.alertType !== 'OrderLate').map((a) => toItem(a));
      const bySref = new Map<string, AlertItem<A>>();
      for (const t of trucks) if (t.alert.sref) bySref.set(t.alert.sref, t);
      const orphans: AlertItem<A>[] = [];
      for (const a of g.alerts.filter((x) => x.alertType === 'OrderLate')) {
        const parent = a.sref ? bySref.get(a.sref) : undefined;
        if (parent) {
          parent.children.push(toItem(a));
          childCount++;
        } else {
          orphans.push(toItem(a)); // truck alert not present: show the order at the top level
        }
      }
      for (const t of trucks) t.children.sort(compareItems);
      items = [...trucks, ...orphans];
    } else {
      items = g.alerts.map((a) => toItem(a));
    }
    items.sort(compareItems);

    const subjects: string[] = [];
    for (const it of items)
      if (it.subject && !subjects.includes(it.subject)) subjects.push(it.subject);

    groups.push({
      key,
      tier: g.tier,
      type: g.type,
      items,
      count: items.length,
      childCount,
      expandable: items.length > 1 || items.some((i) => i.children.length > 0),
      summarySubjects: subjects.slice(0, SUMMARY_MAX),
      summaryMore: Math.max(0, subjects.length - SUMMARY_MAX),
    });
  }

  return TIER_ORDER.map((tier) => {
    const gs = groups.filter((g) => g.tier === tier).sort(compareGroups);
    return { tier, groups: gs, count: gs.reduce((n, g) => n + g.count, 0) };
  }).filter((s) => s.groups.length > 0);
}

/** Counts per tier and in total, for the filter buttons and the bell badge. */
export function tierCounts(sections: AlertSection[]): Record<AlertTier | 'all', number> {
  const c = { all: 0, critical: 0, warning: 0, notice: 0 };
  for (const s of sections) {
    c[s.tier] += s.count;
    c.all += s.count;
  }
  return c;
}

/** Group keys open by default: every expandable critical group. */
export function defaultExpanded(sections: AlertSection[]): Set<string> {
  const open = new Set<string>();
  for (const s of sections) {
    if (s.tier !== 'critical') continue;
    for (const g of s.groups) if (g.expandable) open.add(g.key);
  }
  return open;
}
